import { faCircleCheck, faClock } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Alert } from '@orobix/orobix-fe-components'
import { differenceInDays } from 'date-fns'
import { useTranslation } from 'react-i18next'

import { DataSyncStatus } from '../../../types/dataSync'
import { formatDateTime } from '../../../utils/date'
import { useStyles } from './IngestionStatusBanner.styles'
import { NextCheckLine } from './NextCheckLine'

/**
 * The two healthy states — data fresh, data old — are one banner: the same two
 * facts in the same order, with the colour and a single extra line carrying
 * the difference.
 *
 * They were two components, and drifted: the green one showed the sales window
 * and the last check, the yellow one showed neither and asserted instead that
 * "the synchronization is working" — a sentence derived from nothing, and
 * false whenever the scheduler was the thing that had stopped. The sales
 * window left with them: the launch modal states it under the date fields,
 * where it is actually used.
 */
export const SyncedBanner = ({
  sync,
  stale,
}: {
  sync: DataSyncStatus
  stale: boolean
}) => {
  const classes = useStyles()
  const { t } = useTranslation()

  return (
    <Alert type={stale ? 'warning' : 'success'} withBorder>
      <span className={classes.icon}>
        <FontAwesomeIcon icon={stale ? faClock : faCircleCheck} />
      </span>

      <span>
        <strong>
          {sync.dataReceivedAt &&
            t('Last data received on {{ receivedAt }}', {
              receivedAt: formatDateTime(new Date(sync.dataReceivedAt)),
            })}
        </strong>

        <NextCheckLine nextCheckAt={sync.nextCheckAt} />

        {stale && sync.dataReceivedAt && (
          <>
            <br />
            {/*
              The threshold is 72 hours, but the message reports the real age:
              at day five "more than 72 hours" reads as if the alert had just
              fired.
            */}
            {t(
              'No new data for {{ days }} days: check that the files are being delivered to the server',
              {
                days: differenceInDays(
                  new Date(),
                  new Date(sync.dataReceivedAt)
                ),
              }
            )}
          </>
        )}
      </span>
    </Alert>
  )
}
